import { CertificateResponse } from '@/types/certificate_types';
import { CheckoutApiResponse, OrderListApiResponse, PaymentListApiResponse } from '@/types/order_types';
import axiosInstance from '@/utils/axios';
import axios from 'axios';

// 체크아웃 요청 타입
interface CheckoutRequest {
  cart_item_ids: number[];
}

// 구매 API
export const purchaseApi = {
  /**
   * 선택한 장바구니 항목으로 구매 주문 생성
   * 인증은 axios 인터셉터에서 자동 처리됩니다
   */
  createPurchase: async (cartItemIds: number[]): Promise<CheckoutApiResponse> => {
    try {
      if (!cartItemIds || cartItemIds.length === 0) {
        throw new Error('No items selected from the cart.');
      }

      const requestBody: CheckoutRequest = {
        cart_item_ids: cartItemIds,
      };

      const response = await axiosInstance.post<CheckoutApiResponse>('/orders/checkout/', requestBody);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create order.');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;

        if (error.response?.status === 401) {
          throw new Error('인증이 필요합니다. 로그인해 주세요.');
        } else if (error.response?.status === 400) {
          const errorMessage = errorData?.error?.message || errorData?.message;
          throw new Error(errorMessage || '선택된 항목이 없거나 재고가 부족합니다.');
        } else if (error.response?.status === 409) {
          throw new Error(errorData?.message || '재고가 부족합니다.');
        } else {
          throw new Error(errorData?.message || '주문 생성 중 오류가 발생했습니다.');
        }
      }
      throw new Error('A network error occurred.');
    }
  },

  /**
   * 사용자의 구매 내역 조회
   * 인증은 axios 인터셉터에서 자동 처리됩니다
   */
  getUserPurchases: async (page: number = 1): Promise<OrderListApiResponse> => {
    try {
      const response = await axiosInstance.get<OrderListApiResponse>(`/orders/?status=PAID&page=${page}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch purchase history.');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        throw new Error(errorData?.message || 'Failed to fetch purchase history.');
      }
      throw error;
    }
  },

  /**
   * 결제 내역 조회
   * 인증은 axios 인터셉터에서 자동 처리됩니다
   */
  getPaymentHistory: async (page: number = 1): Promise<PaymentListApiResponse> => {
    try {
      const response = await axiosInstance.get<PaymentListApiResponse>(`/payments/?page=${page}`);

      if (!response.data.success) {
        throw new Error(response.data.message || '결제 내역 조회에 실패했습니다.');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;
        throw new Error(errorData?.message || '결제 내역 조회에 실패했습니다.');
      }
      throw error;
    }
  },

  /**
   * 주문 항목의 구매 증명서 조회
   * 인증은 axios 인터셉터에서 자동 처리됩니다
   */
  fetchCertificate: async (orderItemId: string): Promise<CertificateResponse> => {
    try {
      const response = await axiosInstance.get<CertificateResponse>(`/certificates/${orderItemId}/`);

      if (!response.data.success) {
        throw new Error(response.data.message || '증명서 조회에 실패했습니다.');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data;

        if (error.response?.status === 404) {
          throw new Error('증명서를 찾을 수 없습니다.');
        } else if (error.response?.status === 403) {
          throw new Error('해당 증명서를 조회할 권한이 없습니다.');
        }

        throw new Error(errorData?.message || '증명서 조회 중 오류가 발생했습니다.');
      }
      throw error;
    }
  },
};
